import { extend, media, stylesheet } from 'typestyle';
import { Blue80, Gray100, typeCompact14MediumProperties, typeCompact14RegularProperties } from '@traceair/tds';
import { useDocumentsStore } from '../../../../../store/documentsStore';
import { CoaDocument } from '../../../../../types';
import { format as formatWithFns } from 'date-fns';
import { FileTextOutlineIcon } from '@traceair/webapp-icons';
import { BREAKPOINT_SMALL } from '../../../../layout/styles';
import { blueLinkStates } from '../../../../../styles/linkStates';
import { useBreakpoint } from '../../../../../hooks/useBreakpoint';

const css = stylesheet({
  container: extend(
    {
      whiteSpace: 'nowrap',
    },
    media(
      {
        maxWidth: BREAKPOINT_SMALL,
      },
      {
        display: 'flex',
        alignItems: 'center',
      },
    ),
  ),
  heading: extend(
    {
      marginBottom: 7,
      color: Gray100,
      ...typeCompact14RegularProperties,
    },
    media(
      {
        maxWidth: BREAKPOINT_SMALL,
      },
      {
        margin: '0 3px 0 0',
      },
    ),
  ),
  range: {
    color: Blue80,
    textDecoration: 'none',
    ...typeCompact14MediumProperties,
    ...blueLinkStates,
  },
  icon: {
    marginRight: 4,
    verticalAlign: -4,
  },
});

const getCurrentDocument = (documents: CoaDocument[], projectId: string) => {
  const currentDate = new Date();

  for (const document of documents) {
    if (document.projectId === projectId) {
      const doc = document.document;

      if (doc?.startDate && doc.endDate) {
        const startDate = new Date(doc.startDate);
        const endDate = new Date(doc.endDate);

        if (currentDate >= startDate && currentDate <= endDate) {
          return doc;
        }
      }
    }
  }

  return null;
};

interface Props {
  airtableProjectId?: string;
}

export const MissionWindowCoa = ({ airtableProjectId }: Props) => {
  const { goodCoaDocs } = useDocumentsStore();

  const { smallerThanSmall } = useBreakpoint();

  if (!airtableProjectId) {
    return null;
  }

  const currentDocument = getCurrentDocument(goodCoaDocs, airtableProjectId);
  if (!currentDocument) return null;

  const format = 'd MMM yyyy';

  const start = formatWithFns(new Date(currentDocument.startDate!), format);
  const end = formatWithFns(new Date(currentDocument.endDate!), format);
  const url = currentDocument.attachments?.[0]?.url;

  return (
    <div className={css.container}>
      <div className={css.heading}>{smallerThanSmall ? 'COA:' : 'Certificate of Authorization'}</div>

      <a href={url} target={url ? '_blank' : undefined} className={css.range} rel="noreferrer">
        <span className={css.icon}>
          <FileTextOutlineIcon size={20} color={Blue80} />
        </span>

        {`${start} – ${end}`}
      </a>
    </div>
  );
};
